import {useQuery, UseQueryOptions} from "@tanstack/react-query";
import api from "../lib/api";
import {normalizePage, Page, Sort} from "../types/page";
import {CalenderEvent, EventDetails, EventListing, EventListItem, HostDetails} from "../types/event";

async function fetchBusinessEvents(
    businessId: string,
    number: number,
    size: number,
    sort: Sort,
    status?: string,
    query?: string,
    feedbackEnabled?: boolean,
    cashlessEnabled?: boolean,
    archived?: boolean
): Promise<Page<EventListItem>> {
    const params = new URLSearchParams();
    params.set("page", String(number));
    params.set("size", String(size));
    params.set("sort", `${sort.field},${sort.dir}`);
    if (status) params.set("status", status);
    if (query) params.set("query", query);
    if (feedbackEnabled !== undefined) params.set("feedbackEnabled", String(feedbackEnabled));
    if (cashlessEnabled !== undefined) params.set("cashlessEnabled", String(cashlessEnabled));
    if (archived !== undefined) params.set("archived", String(archived));
    return api.get(`/api/v1/business/${businessId}/events`, {params})
        .then(({data}) => normalizePage<EventListItem>(data));
}

type UseBusinessEventsOpts = {
    page: number;
    size: number;
    sort: Sort;
    status?: string;
    query?: string;
    feedbackEnabled?: boolean;
    /** Narrows the page to events running cashless. */
    cashlessEnabled?: boolean;
    /** Tri-state: leave undefined for both, false for active events, true for archived ones. */
    archived?: boolean;
    queryOptions?: Omit<
        UseQueryOptions<Page<EventListItem>, Error, Page<EventListItem>, readonly unknown[]>,
        "queryKey" | "queryFn"
    >;
}

export function useBusinessEvents(businessId: string, opts: UseBusinessEventsOpts) {
    const {page, size, sort, status, query, feedbackEnabled, cashlessEnabled, archived} = opts;
    return useQuery<Page<EventListItem>, Error>({
        queryKey: ["business-events", businessId, page, size, sort, status ?? null, query ?? null,
            feedbackEnabled ?? null, cashlessEnabled ?? null, archived ?? null],
        queryFn: () => fetchBusinessEvents(businessId, page, size, sort, status, query, feedbackEnabled,
            cashlessEnabled, archived),
        enabled: !!businessId,
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        ...opts.queryOptions,
    });
}

// ---- Fetcher ----
async function fetchEvents(
    number: number,
    size: number,
    sort: Sort,
    featuredOnly?: boolean,
    city?: string,
    category?: string,
    subCategory?: string,
    tags?: string[],
    dateFrom?: string,
    dateTo?: string,
    query?: string,
    nativeOnly?: boolean
): Promise<Page<EventListing>> {
    const params = new URLSearchParams();
    params.set("page", String(number));
    params.set("size", String(size));
    params.set("sort", `${sort.field},${sort.dir}`);
    if (featuredOnly) params.set("featured", "true");
    if (city) params.set("city", city);
    if (category) params.set("category", category);
    if (subCategory) params.set("subCategory", subCategory);
    if (tags?.length) tags.forEach((t) => params.append("tags", t));
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (query) params.set("query", query);
    if (nativeOnly) params.set("nativeOnly", "true");

    return api.get('/api/v2/public/events', {params})
        .then(({data}) => normalizePage<EventListing>(data));
}

// ---- Hooks ----
type UseEventsOpts = {
    page: number;
    size: number;
    sort: Sort;
    featuredOnly?: boolean;
    city?: string;
    category?: string;
    subCategory?: string;
    tags?: string[];
    dateFrom?: string;
    dateTo?: string;
    query?: string;
    /**
     * Narrows the page to events NamEvents sells itself, dropping the listings aggregated from
     * other ticketing sites. Browse and search leave it off so the catalogue stays complete.
     */
    nativeOnly?: boolean;
    queryOptions?: Omit<
        UseQueryOptions<Page<EventListing>, Error, Page<EventListing>, any[]>,
        "queryKey" | "queryFn"
    >;
}

// ---- Hook Implementation ----
export function useEvents(opts: UseEventsOpts) {
    const {page, size, sort, featuredOnly, city, category, subCategory, tags, dateFrom, dateTo, query, nativeOnly} = opts;
    // @ts-ignore
    return useQuery<Page<EventListing>, Error>({
        queryKey: ["public-events", page, size, sort, featuredOnly, city, category, subCategory, tags, dateFrom, dateTo, query, nativeOnly ?? null],
        queryFn: () => fetchEvents(page, size, sort, featuredOnly, city, category, subCategory, tags, dateFrom, dateTo, query, nativeOnly),
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        ...opts.queryOptions,
    });
}

// ---- Single Event Fetcher ----
async function fetchEventById(
    idOrSlug: string
): Promise<EventDetails> {
    return api.get(`/api/v1/public/events/${idOrSlug}`)
        .then(({data}) => data as EventDetails);
}

// ---- Single Event Hook ----
type UseEventByIdOpts = {
    idOrSlug: string;
    queryOptions?: Omit<
        UseQueryOptions<EventDetails, Error, EventDetails, readonly unknown[]>,
        "queryKey" | "queryFn"
    >
}

// ---- Hook Implementation ----
export function useEventById(opts: UseEventByIdOpts) {
    const {idOrSlug} = opts;
    return useQuery<EventDetails, Error>({
        queryKey: ["public-event-by-id", idOrSlug],
        queryFn: () => fetchEventById(idOrSlug),
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        ...opts.queryOptions,
    });
}

// --- Calendar Events Fetcher ---
async function fetchCalendarEvents(
    dateFrom: string,
    dateTo: string,
    city?: string,
    category?: string,
): Promise<CalenderEvent[]> {
    const params: Record<string, string> = {dateFrom, dateTo};
    if (city) params.city = city;
    if (category) params.category = category;
    return api.get('/api/v1/public/events/calender', {params})
        .then(({data}) => data as CalenderEvent[]);
}

// --- Calendar Events Hook ---
type UseCalendarEventsOpts = {
    dateFrom: string | null;
    dateTo: string | null;
    city?: string;
    category?: string;
    queryOptions?: Omit<
        UseQueryOptions<CalenderEvent[], Error, CalenderEvent[], readonly unknown[]>,
        "queryKey" | "queryFn"
    >
}

export function useCalendarEvents(opts: UseCalendarEventsOpts) {
    const {dateFrom, dateTo, city, category} = opts;
    return useQuery<CalenderEvent[], Error>({
        queryKey: ["public-calendar-events", dateFrom, dateTo, city ?? null, category ?? null],
        queryFn: () => fetchCalendarEvents(dateFrom!, dateTo!, city, category),
        enabled: !!dateFrom && !!dateTo,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        ...opts.queryOptions,
    });
}

// ---- Host Details Hook ----

export type HostDetailsArgs = { slug: string; };

export function useHostDetails({slug}: HostDetailsArgs) {
    return useQuery<HostDetails>({
        queryKey: ["host-details", slug],
        enabled: !!slug,
        queryFn: async () => {
            const {data} = await api.get(
                `/api/v1/public/events/host/${encodeURIComponent(slug)}`
            );
            return data as HostDetails;
        },
        staleTime: 60_000,
        retry: false,
    });
}

export type HostEventsArgs = {
    slug: string;
    page: number;
    size: number;
    sort?: Sort;
    archived?: boolean;
    isPrivate?: boolean;
};

export function useHostEvents({slug, page, size, sort, archived, isPrivate}: HostEventsArgs) {
    return useQuery<Page<any>>({
        queryKey: ["host-events", slug, archived ?? null, isPrivate ?? null, page, size, sort?.field, sort?.dir],
        enabled: !!slug,
        queryFn: async () => {
            const res = await api.get(
                `/api/v1/public/events/host/${encodeURIComponent(slug)}/events`,
                {
                    params: {
                        page,
                        size,
                        sort: sort ? `${sort.field},${sort.dir}` : undefined,
                        archived,
                        isPrivate,
                    },
                }
            );

            return normalizePage(res.data, {page, size});
        },
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        retry: false,
    });
}
